import type { CSSProperties, ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import LazyImage from '@/components/ui/LazyImage'
import { kenBurns as kenBurnsPreset } from './motion'

/**
 * The photographic primitive of the marketing kit.
 *
 * Three policies live here so no caller has to remember them:
 *
 *  - SCRIM. Text over a photograph needs a guaranteed floor of contrast. The
 *    standard scrim darkens the bottom of the frame (and a touch of the top) on
 *    a fixed curve, so `text-onmedia` copy is legible whatever the CDN serves.
 *  - PLACEHOLDER. Never a flat grey box. A warm gradient sits under the image
 *    permanently, so a slow load, a blocked CDN, or a 404 all degrade to
 *    something that still looks like the brand. A 404 goes further: the frame
 *    is a `data-photo-frame`, so the failed-photo contract (index.css) hides
 *    the broken image AND this frame's credit chip, leaving the gradient to
 *    stand alone rather than under a glyph and a credit for nothing.
 *  - LAYOUT. Every photo reserves its box (explicit `aspectRatio`, or `width`
 *    plus `height`, or an `aspect-*`/`h-*` class from the caller). No CLS.
 *
 * Ken Burns is the BREATHE tier — opt in with `kenBurns`, and only on one photo
 * per viewport. It degrades to a still frame under `prefers-reduced-motion`.
 */

/** Frozen scrim curve for the direction. Same values on every surface. */
const SCRIM_STANDARD = 'linear-gradient(180deg, rgba(24,17,13,.08), transparent 40%, rgba(24,17,13,.72))'
/**
 * Deeper variant for full-bleed frames carrying a headline. The mid-stop is
 * deliberately heavy: a bright photograph (a sunset sky, a pale horizon) puts
 * near-white behind body copy in the MIDDLE of the frame, where the standard
 * bottom-weighted scrim gives no protection at all.
 */
const SCRIM_STRONG = 'linear-gradient(180deg, rgba(24,17,13,.46), rgba(24,17,13,.40) 45%, rgba(24,17,13,.86))'

/** Warm ground shown before/instead of the image. Never grey. */
const WARM_GROUND = 'bg-gradient-to-br from-clay-300 via-clay-100 to-sand-300'

export type PhotoScrim = 'none' | 'standard' | 'strong'

export interface PhotoProps {
  src: string
  alt: string
  /**
   * Responsive candidates for `src`. Self-hosted frames pass these from the
   * generated manifest; hotlinked Unsplash frames leave them off and let
   * `LazyImage` derive a CDN srcSet on its own.
   */
  srcSet?: string
  /** Format alternatives (AVIF/WebP), best-first. Self-hosted frames only. */
  sources?: readonly { type: string; srcSet: string }[]
  /** Which contrast floor to lay over the frame. Default `standard`. */
  scrim?: PhotoScrim
  /** Ambient slow push-in. BREATHE tier — at most one per viewport. */
  kenBurns?: boolean
  /** Optional photographer/source credit, rendered small in the corner. */
  credit?: string
  /** Overlay content (headline, badges). Rendered above the scrim. */
  children?: ReactNode
  /** Extra classes on the frame — rounding, positioning, sizing. */
  className?: string
  /** Explicit ratio, e.g. `"16/9"` or `1.5`. Ignored when width+height are set. */
  aspectRatio?: string | number
  width?: number
  height?: number
  /** `sizes` for the responsive srcSet. Pass `"100vw"` for full-bleed frames. */
  sizes?: string
  /**
   * Mark as the page's LCP image: eager-loads and jumps the request queue.
   * Exactly one per page.
   */
  priority?: boolean
}

/**
 * Mirrors LazyImage's heuristic: if the caller already constrains the box
 * (aspect-*, h-*, inset-0 for a full-bleed frame), don't impose a ratio on top.
 */
function callerReservesBox(className: string): boolean {
  return /(?:^|\s)(aspect-|h-|inset-0|min-h-)/.test(className)
}

/**
 * The frame needs a positioning context for its absolutely-stacked layers, but
 * a full-bleed caller passes its own (`absolute inset-0`). Both classes would
 * then be present, and Tailwind emits `.relative` AFTER `.absolute`, so the
 * default silently wins the cascade no matter how the class string is ordered —
 * which collapses the frame to 0x0, since every layer inside is absolute and
 * nothing is left to give it height. Yield to a caller-supplied position.
 */
function positionClass(className: string): string {
  return /(?:^|\s)(absolute|fixed|sticky|static|relative)(?:\s|$)/.test(className) ? '' : 'relative'
}

export default function Photo({
  src,
  alt,
  srcSet,
  sources,
  scrim = 'standard',
  kenBurns = false,
  credit,
  children,
  className = '',
  aspectRatio,
  width,
  height,
  sizes,
  priority = false,
}: PhotoProps) {
  const reduceMotion = useReducedMotion()
  const animate = kenBurns && !reduceMotion

  const hasDims = typeof width === 'number' && typeof height === 'number'
  const frameStyle: CSSProperties | undefined = (() => {
    if (hasDims) return undefined
    if (aspectRatio != null) return { aspectRatio: String(aspectRatio) }
    if (!callerReservesBox(className)) return { aspectRatio: '16 / 9' }
    return undefined
  })()

  const kb = kenBurnsPreset()

  return (
    <div
      /* The failed-photo contract (index.css): this box owns the gradient, so
         it is the frame a broken image and its credit chip hide inside. */
      data-photo-frame
      className={`${positionClass(className)} overflow-hidden ${className}`}
      style={frameStyle}
    >
      {/* Warm ground — permanent, so the frame is never empty or grey. */}
      <div aria-hidden className={`absolute inset-0 ${WARM_GROUND}`} />

      {/* An empty `src` is the frame plan's documented last resort (see
          `resolve` in marketingImages.ts). Render no <img> at all and let the
          warm ground stand alone — an image element pointed at nothing
          resolves against the page URL and downloads the document. */}
      {src !== '' && (
        <motion.div
          className="absolute inset-0"
          initial={animate ? kb.initial : undefined}
          animate={animate ? kb.animate : undefined}
          transition={animate ? kb.transition : undefined}
        >
          <LazyImage
            src={src}
            alt={alt}
            srcSet={srcSet}
            sources={sources}
            className="w-full h-full"
            placeholderClassName={WARM_GROUND}
            sizes={sizes}
            width={hasDims ? width : undefined}
            height={hasDims ? height : undefined}
            lazy={!priority}
            fetchPriority={priority ? 'high' : 'auto'}
          />
        </motion.div>
      )}

      {scrim !== 'none' && (
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ backgroundImage: scrim === 'strong' ? SCRIM_STRONG : SCRIM_STANDARD }}
        />
      )}

      {children != null && <div className="relative z-[1] h-full">{children}</div>}

      {credit && (
        <span data-photo-credit className="absolute bottom-2 right-2 z-[2] rounded-full bg-black/30 px-2 py-0.5 text-[10px] leading-none text-onmedia/70 backdrop-blur-sm select-none pointer-events-none">
          {credit}
        </span>
      )}
    </div>
  )
}
